import { motion, AnimatePresence } from 'framer-motion';

// Components
import Picto from '../../atoms/Picto/Picto';
import CheckboxHeader from '../../molecules/CheckboxHeader/CheckboxHeader';
import DefaultHeader from '../../molecules/DefaultHeader/DefaultHeader';

// Utils
import cn from '../../../utils/cn';
import { builtHeadCellOverlapsHoveredLeafIndices } from '../../../domain/table/tableColumnHoverUseCase';
import { useTableColumnHoverStateOptional } from '../../../domain/table/useTableColumnHover';
import { getCommonPinningStyles } from '../../../domain/table/tableUseCase';
import { buildTableHeadRows } from '../../../domain/table/buildTableHeadRows';
import {
  getApiHeaderCellInlineStyle,
  getPinnedAwareVisibleLeafColumns,
  getTableHeadCornerKeys,
  getThSpanWidthPx,
} from '../../../domain/table/tableHeadUseCase';

// Styles
import cornerStyles from '../../../styles/tableCardCorners.module.css';
import styles from './TableHead.module.css';

/**
 * Animation variants for sort icons
 * @type {import('framer-motion').Variants}
 */
const sortIconVariants = {
  initial: {
    scale: 0,
    rotate: -180,
    opacity: 0,
  },
  animate: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
      duration: 0.3,
    },
  },
  exit: {
    scale: 0,
    rotate: 180,
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
};

/**
 * TableHead component for TanStack React Table (multi-row API headers + colspan + rowspan)
 * @param {object} props
 * @param {TableInstance} props.table - TanStack table instance
 * @param {boolean} [props.isVisible=true] - Whether to show the thead element
 * @param {() => void} [props.handleSelectAllRows] - Handle select all rows
 * @param {boolean} [props.allRowsAreSelected] - All rows are selected
 * @param {boolean} [props.selectAllRowsIndeterminate] - Partial row selection (header checkbox)
 * @returns {JSX.Element}
 */
function TableHead({
  table,
  isVisible = true,
  handleSelectAllRows,
  allRowsAreSelected,
  selectAllRowsIndeterminate = false,
}) {
  const { hoveredLeafIndices } = useTableColumnHoverStateOptional();
  const leafColumns = getPinnedAwareVisibleLeafColumns(table);
  const headRows = buildTableHeadRows(leafColumns);
  const firstHeaderGroup = table.getHeaderGroups()[0];
  const tanstackHeaders = firstHeaderGroup?.headers ?? [];
  const headerByColumnId = new Map(
    tanstackHeaders.map((h) => [h.column.id, h]),
  );

  return (
    <thead
      className={styles['table-head']}
      style={{ display: isVisible ? 'table-header-group' : 'none' }}
    >
      {headRows.map((row, rowIndex) => (
        <tr className={styles['table-head-row']} key={row.rowKey}>
          {row.cells.map((built, cellIndex) => {
            const column = table.getColumn(built.columnId);
            if (!column) return null;

            const header = headerByColumnId.get(built.columnId);
            const typedColumnDef = /** @type {TableColumnsReactTable} */ (column.columnDef);
            const isColumnCheckbox = typedColumnDef.type === 'static-checkbox';
            const pinStyles = getCommonPinningStyles(column);
            const widthPx = getThSpanWidthPx(table, built.columnId, built.colspan);
            const mergedStyle = {
              ...pinStyles,
              ...getApiHeaderCellInlineStyle(built.header),
              width: widthPx,
              maxWidth: widthPx,
            };
            const isColumnAddingOptimistic = typedColumnDef.isAddingColumnOptimistic ?? false;

            const isLeafSortable = built.kind === 'leaf'
              && !isColumnCheckbox
              && column.getCanSort();
            // Single-header columns in a multi-row thead render as `api` with rowspan (not `leaf`);
            // they still need the TanStack resizer like the bottom-row leaf cells.
            const isFullHeightSingleHeaderApi = built.kind === 'api'
              && built.colspan === 1
              && built.rowspan > 1;
            const showResizer = !isColumnCheckbox && header && column.getCanResize() && (
              built.kind === 'leaf' || isFullHeightSingleHeaderApi
            );

            const thKey = `h-${rowIndex}-${cellIndex}-${built.columnId}`;
            /** @type {import('react').CSSProperties} */
            const headerLabelAlignStyle = {
              textAlign: built.header.align ?? 'left',
            };
            const cornerKeys = getTableHeadCornerKeys({
              rowIndex,
              rowspan: built.rowspan,
              headRowCount: headRows.length,
              leafStartIndex: built.leafStartIndex,
              colspan: built.colspan,
              totalLeafCount: leafColumns.length,
            });
            // Top and bottom masks cannot share one ::after (cells already use ::before for the edge).
            const topCornerClassNames = cornerKeys
              .filter((k) => k === 'tl' || k === 'tr')
              .map((k) => cornerStyles[`corner-${k}`]);
            const bottomCornerKeys = cornerKeys.filter((k) => k === 'bl' || k === 'br');

            const isColumnHovered = builtHeadCellOverlapsHoveredLeafIndices(built, hoveredLeafIndices);

            if (built.kind === 'api') {
              return (
                <th
                  key={thKey}
                  className={cn([
                    styles['table-head-cell'],
                    styles['table-head-cell-api'],
                    ...topCornerClassNames,
                    column.getIsPinned() && styles['table-head-cell-pinned'],
                    isColumnAddingOptimistic && styles['table-head-cell-adding-optimistic'],
                    isColumnHovered && styles['table-head-cell-column-hover'],
                  ])}
                  colSpan={built.colspan}
                  rowSpan={built.rowspan}
                  style={mergedStyle}
                >
                  {bottomCornerKeys.map((k) => (
                    <span
                      key={k}
                      className={cn([styles['table-head-corner'], cornerStyles[`corner-${k}`]])}
                      aria-hidden="true"
                    />
                  ))}
                  <div
                    className={styles['table-head-cell-content']}
                    style={headerLabelAlignStyle}
                  >
                    {built.header.lib}
                  </div>
                  {showResizer ? (
                    <div
                      className={cn([
                        styles['table-head-resizer'],
                        table.options.columnResizeDirection,
                        column.getIsResizing() && styles['is-resizing'],
                      ])}
                      onDoubleClick={() => column.resetSize()}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      style={table.options.columnResizeMode === 'onEnd'
                        && column.getIsResizing()
                        ? {
                          transform: `translateX(${(table.options.columnResizeDirection === 'rtl'
                            ? -1
                            : 1)
                          * (table.getState().columnSizingInfo.deltaOffset ?? 0)}px)`,
                        }
                        : undefined}
                      role="presentation"
                    />
                  ) : null}
                </th>
              );
            }

            if (built.kind === 'checkbox-span') {
              return (
                <th
                  key={thKey}
                  className={cn([
                    styles['table-head-cell'],
                    styles['table-head-cell-checkbox'],
                    ...topCornerClassNames,
                    column.getIsPinned() && styles['table-head-cell-pinned'],
                    isColumnHovered && styles['table-head-cell-column-hover'],
                  ])}
                  colSpan={built.colspan}
                  rowSpan={built.rowspan}
                  style={mergedStyle}
                >
                  {bottomCornerKeys.map((k) => (
                    <span
                      key={k}
                      className={cn([styles['table-head-corner'], cornerStyles[`corner-${k}`]])}
                      aria-hidden="true"
                    />
                  ))}
                  <div className={styles['table-head-cell-content']}>
                    <CheckboxHeader
                      id={`header-${column.id}`}
                      value={allRowsAreSelected}
                      indeterminate={selectAllRowsIndeterminate}
                      onChange={handleSelectAllRows}
                      size="small"
                      align="center"
                      theme="black"
                    />
                  </div>
                  <div
                    className={cn([
                      styles['table-head-resizer'],
                      table.options.columnResizeDirection,
                      column.getIsResizing() && styles['is-resizing'],
                      styles['table-head-cell-checkbox-resizer'],
                    ])}
                    onDoubleClick={() => column.resetSize()}
                    onMouseDown={header?.getResizeHandler()}
                    onTouchStart={header?.getResizeHandler()}
                    style={table.options.columnResizeMode === 'onEnd'
                      && column.getIsResizing()
                      ? {
                        transform: `translateX(${(table.options.columnResizeDirection === 'rtl'
                          ? -1
                          : 1)
                          * (table.getState().columnSizingInfo.deltaOffset ?? 0)}px)`,
                      }
                      : undefined}
                    role="presentation"
                  />
                </th>
              );
            }

            // leaf
            /** @type {import('react').ReactNode} */
            let leafMainContent;
            if (isColumnCheckbox) {
              leafMainContent = (
                <div className={styles['table-head-cell-content']}>
                  <CheckboxHeader
                    id={`header-${column.id}`}
                    value={allRowsAreSelected}
                    indeterminate={selectAllRowsIndeterminate}
                    onChange={handleSelectAllRows}
                    size="small"
                    align="center"
                    theme="black"
                  />
                </div>
              );
            } else {
              leafMainContent = (
                <button
                  type="button"
                  className={cn([
                    styles['table-head-cell-button'],
                    isLeafSortable ? styles['table-head-cell-button-sortable'] : '',
                  ])}
                  onClick={column.getToggleSortingHandler()}
                >
                  <div
                    className={styles['table-head-cell-content']}
                    style={headerLabelAlignStyle}
                  >
                    <DefaultHeader title={built.header.lib}>
                      {built.header.lib}
                    </DefaultHeader>
                  </div>

                  <div className={styles['table-head-cell-actions']}>
                    <AnimatePresence mode="wait">
                      {column.getIsSorted() === 'asc' && (
                        <motion.div
                          key="asc"
                          variants={sortIconVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Picto icon="arrow-down-bulk" />
                        </motion.div>
                      )}
                      {column.getIsSorted() === 'desc' && (
                        <motion.div
                          key="desc"
                          variants={sortIconVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Picto icon="arrow-up-bulk" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </button>
              );
            }

            return (
              <th
                key={thKey}
                className={cn([
                  styles['table-head-cell'],
                  ...topCornerClassNames,
                  column.getIsPinned() && styles['table-head-cell-pinned'],
                  isColumnCheckbox && styles['table-head-cell-checkbox'],
                  isColumnAddingOptimistic && styles['table-head-cell-adding-optimistic'],
                  isColumnHovered && styles['table-head-cell-column-hover'],
                ])}
                colSpan={built.colspan}
                rowSpan={built.rowspan}
                style={mergedStyle}
              >
                {bottomCornerKeys.map((k) => (
                  <span
                    key={k}
                    className={cn([styles['table-head-corner'], cornerStyles[`corner-${k}`]])}
                    aria-hidden="true"
                  />
                ))}
                {leafMainContent}
                {showResizer ? (
                  <div
                    className={cn([
                      styles['table-head-resizer'],
                      table.options.columnResizeDirection,
                      column.getIsResizing() && styles['is-resizing'],
                      isColumnCheckbox && styles['table-head-cell-checkbox-resizer'],
                    ])}
                    onDoubleClick={() => column.resetSize()}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    style={table.options.columnResizeMode === 'onEnd'
                      && column.getIsResizing()
                      ? {
                        transform: `translateX(${(table.options.columnResizeDirection === 'rtl'
                          ? -1
                          : 1)
                          * (table.getState().columnSizingInfo.deltaOffset ?? 0)}px)`,
                      }
                      : undefined}
                    role="presentation"
                  />
                ) : null}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

export default TableHead;
